import React, { useState, useEffect } from "react";
import axios from "axios";
import { getEmployeeCardStyle } from "../utils/employeeColors";
import EmployeeAvailabilityModal from "./EmployeeAvailabilityModal";
import AvailabilityBadge from "./AvailabilityBadge";
import DayAvailability from "./DayAvailability";
import AbsenceCalendar from "./AbsenceCalendar";
import { useAppConfig } from "../context/AppConfigContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : window.location.origin + '/api';

const GestionEquipes = () => {
  const { appConfig } = useAppConfig();
  const companyName = appConfig?.company_name || 'notre équipe';
  const [missions, setMissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [housings, setHousings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  
  // États pour les dates dépliées (par défaut, seules les dates futures/aujourd'hui sont dépliées)
  const [expandedDates, setExpandedDates] = useState({});
  
  // Modal de déplacement
  const [moveModal, setMoveModal] = useState({ open: false, missionId: null, newDate: "" });
  
  // Modal de confirmation annulation
  const [cancelModal, setCancelModal] = useState({ open: false, missionId: null, missionName: "" });

  // État pour le hint de rotation sur mobile
  const [showRotateHint, setShowRotateHint] = useState(true);
  
  // Template WhatsApp — signature dynamique basée sur le tenant courant
  const [whatsappTemplate, setWhatsappTemplate] = useState(
    `Bonjour, le logement au {adresse} est pret.\nRAS,\n${companyName}`
  );

  // Modal de disponibilité employé
  const [availabilityModalEmployee, setAvailabilityModalEmployee] = useState(null);
  const [showAbsenceCalendar, setShowAbsenceCalendar] = useState(false);

  useEffect(() => {
    loadData();
    loadWhatsappTemplate();
  }, []);

  const loadWhatsappTemplate = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp-template`);
      if (res.data.template) setWhatsappTemplate(res.data.template);
    } catch (e) { /* use default */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [missionsRes, employeesRes, housingsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/missions?fields=light`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/housings?fields=light`),
        axios.get(`${API}/clients`)
      ]);
      
      setMissions(missionsRes.data || []);
      setEmployees(employeesRes.data || []);
      setHousings(housingsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error("Erreur chargement:", error);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si une date est passée
  const isDatePast = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Vérifier si une date est aujourd'hui
  const isDateToday = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date.getTime() === today.getTime();
  };

  // Grouper les missions par date puis par employé
  const getMissionsByDateAndEmployee = () => {
    const grouped = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Inclure les 7 derniers jours + 14 jours futurs
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    missions.forEach(m => {
      const mission = m.mission || m;
      const date = mission.date_mission;
      if (!date) return;
      
      // Ignorer complètement les missions annulées
      if (mission.status === 'annule') return;
      
      const missionDate = new Date(date);
      if (missionDate < sevenDaysAgo) return; // Ignorer les missions trop anciennes
      
      if (!grouped[date]) grouped[date] = {};
      
      const housing = m.housing || housings.find(h => h.id === mission.housing_id);
      const employee = employees.find(e => e.id === mission.employee_id);
      const client = housing ? clients.find(c => c.id === housing.client_id) : null;
      const employeeKey = employee?.id || 'non_assigne';
      
      if (!grouped[date][employeeKey]) {
        grouped[date][employeeKey] = {
          employee,
          missions: []
        };
      }
      
      grouped[date][employeeKey].missions.push({
        id: mission.id,
        nom: housing?.pseudo || mission.notes || 'Mission',
        adresse: housing ? `${housing.adresse || ''}, ${housing.ville || ''}`.replace(/^,\s*|,\s*$/g, '') : '',
        client_telephone: housing?.telephone_proprietaire || client?.telephone || null,
        housing_id: mission.housing_id,
        employeeId: mission.employee_id,
        isDivers: !mission.housing_id,
        notes: mission.notes,
        status: mission.status,
        icalOrphan: !!mission.ical_orphan,
        appartient_a: housing?.appartient_a || null,
        logement_pret: mission.logement_pret || false,
        informations_employe: mission.informations_employe || '',
        informations_complementaires: mission.informations_complementaires || '',
        parures_sales: mission.parures_sales || 0,
        parures_notes: mission.parures_notes || '',
        whatsapp_sent: mission.whatsapp_sent || false,
        creneau_debut: housing?.creneau_debut || mission.heure_debut || null,
        creneau_fin: housing?.creneau_fin || mission.heure_fin || null
      });
    });
    
    // Convertir et trier par date
    const result = Object.entries(grouped)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([dateStr, employeesData]) => {
        // Trier les employés : propriétaires en premier (alpha), intervenants ensuite (alpha), non-assigné en dernier
        const sortedEmployees = Object.entries(employeesData).sort(([keyA, dataA], [keyB, dataB]) => {
          const empA = dataA.employee;
          const empB = dataB.employee;
          
          const getEmployeePriority = (emp, key) => {
            if (key === 'non_assigne') return 99;   // Non assigné en dernier
            if (!emp) return 98;
            // Propriétaires en premier, intervenants ensuite
            return (emp.type_employe === 'proprietaire') ? 0 : 1;
          };
          
          const priorityA = getEmployeePriority(empA, keyA);
          const priorityB = getEmployeePriority(empB, keyB);
          
          if (priorityA !== priorityB) return priorityA - priorityB;
          
          // Tri alphabétique pour les employés de même priorité
          return (empA?.prenom || '').localeCompare(empB?.prenom || '');
        });
        
        return {
          date: dateStr,
          isPast: isDatePast(dateStr),
          isToday: isDateToday(dateStr),
          employees: sortedEmployees.map(([key, data]) => ({
            key,
            employee: data.employee,
            // Trier les missions : DIVERS en premier, annulées en dernier
            missions: data.missions.sort((a, b) => {
              // Annulées toujours en dernier
              if (a.status === 'annule' && b.status !== 'annule') return 1;
              if (a.status !== 'annule' && b.status === 'annule') return -1;
              // DIVERS en premier
              if (a.isDivers && !b.isDivers) return -1;
              if (!a.isDivers && b.isDivers) return 1;
              // Tri alphabétique
              return (a.nom || '').localeCompare(b.nom || '');
            })
          }))
        };
      });
    
    // Séparer les jours passés des jours actuels/futurs
    const pastDays = result.filter(d => d.isPast);
    const currentAndFutureDays = result.filter(d => !d.isPast);
    
    // Placer le bloc des jours passés AVANT les jours actuels/futurs
    if (pastDays.length > 0) {
      const totalPastMissions = pastDays.reduce((sum, day) => 
        sum + day.employees.reduce((s, e) => s + e.missions.length, 0), 0
      );
      
      return [
        {
          date: 'PAST_GROUPED',
          isPast: true,
          isGrouped: true,
          label: `📁 Jours terminés (${pastDays.length} jour${pastDays.length > 1 ? 's' : ''} • ${totalPastMissions} mission${totalPastMissions > 1 ? 's' : ''})`,
          pastDays: pastDays
        },
        ...currentAndFutureDays
      ];
    }
    
    return result;
  };

  // Formater la date
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const jours = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    return `${jours[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
  };

  // Toggle expansion d'une date
  const toggleDateExpansion = (dateStr) => {
    setExpandedDates(prev => ({
      ...prev,
      [dateStr]: !prev[dateStr]
    }));
  };

  // Vérifier si une date est dépliée
  const isDateExpanded = (dateStr, isPast) => {
    // Par défaut : dates passées repliées, dates futures/aujourd'hui dépliées
    if (expandedDates[dateStr] !== undefined) {
      return expandedDates[dateStr];
    }
    return !isPast;
  };

  // Changer l'employé
  const changeEmployee = async (missionId, employeeId, opts = {}) => {
    try {
      const url = opts.force
        ? `${API}/missions/${missionId}?force=true`
        : `${API}/missions/${missionId}`;
      await axios.put(url, { employee_id: employeeId || null });
      
      setMissions(prev => prev.map(m => {
        const mission = m.mission || m;
        if (mission.id === missionId) {
          return { ...m, mission: { ...mission, employee_id: employeeId } };
        }
        return m;
      }));
      
      showNotification("✓ Modifié", "success");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.type === "employee_unavailable") {
        const confirmMsg = `⚠️ ${detail.message}\n\nVoulez-vous forcer l'attribution malgré tout ?`;
        if (window.confirm(confirmMsg)) {
          await changeEmployee(missionId, employeeId, { force: true });
        }
        return;
      }
      showNotification("Erreur", "error");
    }
  };

  // Déplacer une mission
  const moveMission = async () => {
    if (!moveModal.newDate) return;
    
    try {
      await axios.put(`${API}/missions/${moveModal.missionId}`, { date_mission: moveModal.newDate });
      
      setMissions(prev => prev.map(m => {
        const mission = m.mission || m;
        if (mission.id === moveModal.missionId) {
          return { ...m, mission: { ...mission, date_mission: moveModal.newDate } };
        }
        return m;
      }));
      
      showNotification("✓ Déplacé", "success");
      setMoveModal({ open: false, missionId: null, newDate: "" });
    } catch (error) {
      if (error.moveCancelled) return;
      showNotification("Erreur", "error");
    }
  };

  // Supprimer une mission (au lieu de juste l'annuler)
  const cancelMission = async () => {
    try {
      // Supprimer complètement la mission
      await axios.delete(`${API}/missions/${cancelModal.missionId}`);
      
      // Retirer de la liste locale
      setMissions(prev => prev.filter(m => {
        const mission = m.mission || m;
        return mission.id !== cancelModal.missionId;
      }));
      
      showNotification("✓ Mission supprimée", "success");
      setCancelModal({ open: false, missionId: null, missionName: "" });
    } catch (error) {
      console.error("Erreur suppression:", error);
      showNotification("Erreur lors de la suppression", "error");
    }
  };

  // Générer les dates futures
  const futureDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const jours = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
      dates.push({
        iso: d.toISOString().split('T')[0],
        label: `${jours[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`
      });
    }
    return dates;
  };

  const showNotification = (msg, type) => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 2000);
  };

  // Marquer une mission comme "WhatsApp envoyé" (propriétaire prévenu)
  const markWhatsappSent = async (missionId) => {
    // Optimiste : mettre à jour localement
    setMissions(prev => prev.map(m => {
      const mission = m.mission || m;
      if (mission.id === missionId) {
        return { ...m, mission: { ...mission, whatsapp_sent: true } };
      }
      return m;
    }));
    try {
      await axios.put(`${API}/missions/${missionId}`, { whatsapp_sent: true });
    } catch (e) {
      console.error("Erreur marquage whatsapp:", e);
    }
  };

  // Trier les employés : M&M en premier
  const sortedEmployees = [...employees].sort((a, b) => {
    const aIsMM = a.prenom?.toLowerCase().includes('m&m');
    const bIsMM = b.prenom?.toLowerCase().includes('m&m');
    if (aIsMM && !bIsMM) return -1;
    if (!aIsMM && bIsMM) return 1;
    return (a.prenom || '').localeCompare(b.prenom || '');
  });

  // Compter les missions actives d'une date
  const countActiveMissions = (employeesData) => {
    return employeesData.reduce((total, { missions }) => 
      total + missions.filter(m => m.status !== 'annule').length, 0);
  };

  const dataByDate = getMissionsByDateAndEmployee();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-2 px-2 md:py-4 md:px-4" data-testid="gestion-equipes-page">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg text-sm text-white shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}>
          {notification.message || notification.msg}
        </div>
      )}

      {/* Modal déplacer */}
      {moveModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-lg p-4 w-full md:w-80 shadow-xl">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 md:hidden"></div>
            <h3 className="font-semibold mb-3 text-center">📅 Déplacer à</h3>
            <select
              value={moveModal.newDate}
              onChange={(e) => setMoveModal(prev => ({ ...prev, newDate: e.target.value }))}
              className="w-full p-3 border rounded-lg mb-4 text-base"
              data-testid="move-date-select"
            >
              <option value="">Choisir une date</option>
              {futureDates().map(d => (
                <option key={d.iso} value={d.iso}>{d.label}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                onClick={() => setMoveModal({ open: false, missionId: null, newDate: "" })}
                className="flex-1 px-4 py-3 text-gray-600 bg-gray-100 rounded-lg font-medium"
                data-testid="move-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={moveMission}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium"
                data-testid="move-confirm-btn"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation suppression */}
      {cancelModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-lg p-4 w-full md:w-80 shadow-xl">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 md:hidden"></div>
            <h3 className="font-semibold mb-2 text-center text-red-600">🗑️ Supprimer la mission ?</h3>
            <p className="text-center text-gray-600 mb-4 text-sm">
              <strong>{cancelModal.missionName}</strong>
              <br />
              Cette mission sera définitivement supprimée du planning et de la facturation.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCancelModal({ open: false, missionId: null, missionName: "" })}
                className="flex-1 px-4 py-3 text-gray-600 bg-gray-100 rounded-lg font-medium"
              >
                Non
              </button>
              <button
                onClick={cancelMission}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium"
              >
                Oui, supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hint de rotation pour mobile */}
      {showRotateHint && (
        <div className="md:hidden fixed inset-0 z-50 bg-slate-900/95 flex flex-col items-center justify-center p-6">
          <div className="animate-bounce mb-6">
            <svg 
              className="w-24 h-24 text-amber-400" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="1.5"
            >
              {/* Icône téléphone avec flèche de rotation */}
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2" strokeLinecap="round" />
              <path d="M20 12l2-2m0 0l2 2m-2-2v6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-amber-400 text-xl font-bold mb-3">
            📱 Tournez votre téléphone
          </div>
          <p className="text-white text-sm mb-6 max-w-xs text-center">
            Pour une meilleure expérience de la sous-traitance, passez en mode paysage (horizontal)
          </p>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-20 border-2 border-slate-500 rounded-lg flex items-center justify-center">
              <span className="text-slate-500 text-xs">📱</span>
            </div>
            <span className="text-amber-400 text-2xl">→</span>
            <div className="w-20 h-12 border-2 border-amber-400 rounded-lg flex items-center justify-center">
              <span className="text-amber-400 text-xs">📱</span>
            </div>
          </div>
          <button
            onClick={() => setShowRotateHint(false)}
            className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
          >
            Continuer quand même
          </button>
        </div>
      )}

      {/* Titre */}
      <div className="flex items-center justify-between mb-3 px-1">
        <h1 className="text-base md:text-lg font-bold text-gray-800" data-testid="page-title">
          🤝 Sous-traitance
        </h1>
        <button
          onClick={() => setShowAbsenceCalendar(v => !v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${showAbsenceCalendar ? 'bg-slate-700 text-white' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
          data-testid="toggle-absence-calendar"
        >
          📅 {showAbsenceCalendar ? 'Masquer le calendrier' : 'Calendrier des absences'}
        </button>
      </div>

      {showAbsenceCalendar && <AbsenceCalendar employees={sortedEmployees} />}

      {/* Liste par date puis par employé */}
      <div className="space-y-3">
        {dataByDate.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Aucune mission</p>
        ) : (
          dataByDate.map((dayData) => {
            const { date: dateStr, isPast, isToday, isGrouped, label, pastDays, employees: employeesData } = dayData;
            
            // Cas spécial : bloc des jours passés groupés
            if (isGrouped) {
              const expanded = expandedDates['PAST_GROUPED'];
              return (
                <div key="past-grouped" className="border rounded-xl overflow-hidden shadow-sm opacity-75">
                  <div 
                    className="px-3 py-2 text-sm font-bold text-white flex items-center justify-between cursor-pointer bg-gray-500 hover:bg-gray-600"
                    onClick={() => setExpandedDates(prev => ({ ...prev, 'PAST_GROUPED': !prev['PAST_GROUPED'] }))}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{expanded ? '▼' : '▶'}</span>
                      <span>{label}</span>
                    </div>
                  </div>
                  
                  {expanded && (
                    <div className="bg-gray-50 p-2 space-y-2">
                      {pastDays.map((pastDay) => (
                        <div key={pastDay.date} className="border rounded-lg overflow-hidden bg-white">
                          <div className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm font-medium flex justify-between">
                            <span>{formatDate(pastDay.date)}</span>
                            <span className="text-xs">{pastDay.employees.reduce((s, e) => s + e.missions.length, 0)} missions</span>
                          </div>
                          <div className="p-2 space-y-1">
                            {pastDay.employees.map(({ key, employee, missions: empMissions }) => (
                              <div key={key} className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-700">
                                  {employee ? `${employee.prenom}` : '⚠️ Non assigné'}
                                </span>
                                <span className="text-gray-500">:</span>
                                <span className="text-gray-600 truncate">
                                  {empMissions.map(m => m.nom).join(', ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            
            // Cas normal : jour individuel (aujourd'hui ou futur)
            const expanded = isDateExpanded(dateStr, isPast);
            const activeMissionsCount = countActiveMissions(employeesData);
            
            return (
              <div key={dateStr} className={`border rounded-xl overflow-hidden shadow-sm ${isPast ? 'opacity-75' : ''}`} data-testid={`date-group-${dateStr}`}>
                {/* En-tête date - cliquable pour les dates passées */}
                <div 
                  className={`px-3 py-2 text-sm font-bold text-white flex items-center justify-between cursor-pointer ${
                    isToday ? 'bg-amber-500' : isPast ? 'bg-gray-400' : 'bg-slate-700'
                  }`}
                  onClick={() => isPast && toggleDateExpansion(dateStr)}
                >
                  <div className="flex items-center gap-2">
                    {isPast && (
                      <span className="text-lg">{expanded ? '▼' : '▶'}</span>
                    )}
                    <span>{formatDate(dateStr)}</span>
                    {isToday && <span className="text-xs font-normal opacity-90">• Aujourd'hui</span>}
                    {isPast && <span className="text-xs font-normal opacity-90">• Terminé</span>}
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                    {activeMissionsCount} mission{activeMissionsCount > 1 ? 's' : ''}
                  </span>
                </div>

                {/* Disponibilité des employés ce jour (disponibles + indisponibles avec raison) */}
                <DayAvailability employees={sortedEmployees} dateStr={dateStr} />

                {/* Contenu - visible si déplié */}
                {expanded && (
                  <div>
                    {employeesData.map(({ key, employee, missions: empMissions }) => {
                      // hasHousing: true → null employee ID renvoie UNASSIGNED (gris), pas DIVERS (rose)
                      const style = getEmployeeCardStyle(employee?.id, { hasHousing: true });
                      return (
                      <div key={key}>
                        {/* En-tête employé avec couleur unique par employé (miroir du planning WeeklyTimeline) */}
                        <div className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 border-t ${style.solidBg} ${style.solidText} ${style.solidBorder}`}>
                          <span>{employee ? `${employee.prenom || ''} ${employee.nom || ''}`.trim() : 'Non assigné'}</span>
                          {employee && (
                            <>
                              <AvailabilityBadge employee={employee} size="xs" date={dateStr} />
                              <button
                                onClick={(e) => { e.stopPropagation(); setAvailabilityModalEmployee(employee); }}
                                className="ml-0.5 p-0.5 rounded hover:bg-white/40 text-gray-700"
                                title="Modifier la disponibilité"
                                data-testid={`availability-gear-${employee.id}`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="3"/>
                                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                </svg>
                              </button>
                            </>
                          )}
                          <span className="ml-auto text-[10px] opacity-70 bg-white/50 px-1.5 py-0.5 rounded-full">
                            {empMissions.filter(m => m.status !== 'annule').length} mission{empMissions.filter(m => m.status !== 'annule').length > 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        {/* Missions */}
                        <div className="divide-y divide-gray-100">
                          {empMissions.map((mission) => {
                            // Bordure colorée selon le propriétaire du logement (dynamique par slug).
                            // Mapping littéral (classes complètes) pour que Tailwind les détecte.
                            const OWNER_BORDERS = [
                              'border-l-4 border-l-blue-500',
                              'border-l-4 border-l-violet-500',
                              'border-l-4 border-l-emerald-500',
                              'border-l-4 border-l-amber-500',
                              'border-l-4 border-l-rose-500',
                              'border-l-4 border-l-cyan-500',
                              'border-l-4 border-l-orange-500',
                              'border-l-4 border-l-fuchsia-500',
                            ];
                            const ownerSlug = (mission.appartient_a || '').toLowerCase().trim();
                            let ownerBorderClass = '';
                            if (ownerSlug) {
                              let hash = 0;
                              for (let i = 0; i < ownerSlug.length; i++) hash = ((hash << 5) - hash + ownerSlug.charCodeAt(i)) | 0;
                              ownerBorderClass = OWNER_BORDERS[Math.abs(hash) % OWNER_BORDERS.length];
                            }
                            
                            return (
                              <div 
                                key={mission.id} 
                                className={`px-2 py-2 flex items-center gap-2 ${ownerBorderClass} hover:bg-gray-50 active:bg-gray-100`}
                                data-testid={`mission-row-${mission.id}`}
                              >
                                {/* Pastille statut : ✅ effectué ou 🔴 à faire */}
                                {!mission.isDivers && (
                                  mission.status === 'effectue' ? (
                                    <span className="text-lg" title="Mission effectuée">
                                      ✅
                                    </span>
                                  ) : (
                                    <span className="w-3 h-3 rounded-full bg-red-500 shadow-sm flex-shrink-0" title="À faire"></span>
                                  )
                                )}

                                {mission.icalOrphan && (
                                  <span
                                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800 font-semibold flex-shrink-0"
                                    title="Réservation introuvable dans le calendrier iCal — à vérifier"
                                    data-testid={`ical-orphan-badge-${mission.id}`}
                                  >
                                    ⚠️ Résa à checker
                                  </span>
                                )}
                                
                                {/* Nom - barré si WhatsApp envoyé */}
                                {mission.isDivers ? (
                                  <span className="text-sm font-medium truncate flex-1 text-red-600">
                                    🔴 {mission.notes || 'DIVERS'}
                                  </span>
                                ) : (
                                  <span className={`text-sm font-medium truncate flex-1 ${
                                    mission.whatsapp_sent 
                                      ? 'line-through text-gray-400' 
                                      : 'text-gray-800'
                                  }`}>
                                    {mission.nom}
                                  </span>
                                )}

                                {/* Actions */}
                                <div className="flex items-center gap-1.5 flex-shrink-0">

                                  {/* Bouton WhatsApp - visible uniquement si mission effectuée et pas DIVERS */}
                                  {mission.status === 'effectue' && !mission.isDivers && !mission.whatsapp_sent && (
                                    (() => {
                                      const phone = mission.client_telephone ? mission.client_telephone.replace(/\s/g, '').replace(/^0/, '33') : '';
                                      const msg = encodeURIComponent(whatsappTemplate.replace('{adresse}', mission.adresse || ''));
                                      const waUrl = phone 
                                        ? `https://api.whatsapp.com/send?phone=${phone}&text=${msg}`
                                        : `https://api.whatsapp.com/send?text=${msg}`;
                                      return (
                                        <a
                                          href={waUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={() => setTimeout(() => markWhatsappSent(mission.id), 500)}
                                          className="p-2 rounded-lg bg-green-100 hover:bg-green-200 active:bg-green-300 transition-colors"
                                          title={`WhatsApp → ${mission.client_telephone || 'pas de numéro'}`}
                                          data-testid={`whatsapp-btn-${mission.id}`}
                                        >
                                          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.612.616l4.584-1.468A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.386 0-4.586-.818-6.334-2.187l-.444-.347-3.073.983.983-3.073-.347-.444A9.959 9.959 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                                        </a>
                                      );
                                    })()
                                  )}

                                  {/* Badge "Envoyé" si WhatsApp déjà envoyé */}
                                  {mission.whatsapp_sent && (
                                    <span className="text-xs text-green-500 font-medium px-1">Envoyé</span>
                                  )}

                                  {/* Sélecteur employé */}
                                  <select
                                    value={mission.employeeId || ''}
                                    onChange={(e) => changeEmployee(mission.id, e.target.value || null)}
                                    className="text-xs px-1.5 py-1.5 rounded border cursor-pointer bg-gray-50"
                                  >
                                    <option value="">—</option>
                                    {sortedEmployees.map(emp => (
                                      <option key={emp.id} value={emp.id}>
                                        {emp.prenom} {emp.nom || ''}
                                      </option>
                                    ))}
                                  </select>

                                  {/* Bouton déplacer */}
                                  <button
                                    onClick={() => setMoveModal({ open: true, missionId: mission.id, newDate: "" })}
                                    className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-600"
                                    title="Déplacer"
                                    data-testid={`move-btn-${mission.id}`}
                                  >
                                    📅
                                  </button>

                                  {/* Bouton annuler */}
                                  <button
                                    onClick={() => setCancelModal({ open: true, missionId: mission.id, missionName: mission.isDivers ? mission.notes : mission.nom })}
                                    className="p-2 rounded-lg bg-red-100 hover:bg-red-200 active:bg-red-300 text-red-600"
                                    title="Annuler"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {availabilityModalEmployee && (
        <EmployeeAvailabilityModal
          employee={availabilityModalEmployee}
          onClose={() => setAvailabilityModalEmployee(null)}
          onSaved={(updated) => {
            setEmployees(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
          }}
        />
      )}
    </div>
  );
};

export default GestionEquipes;
/* Deployment trigger - Sun Jan  4 16:59:10 UTC 2026 */
